// src/components/Transaction/TransactionForm.js - Enhanced responsive version
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TextField, 
  FormControl, 
  InputLabel, 
  Button,
  Box,
  Stack,
  Typography,
  InputAdornment,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Select,
  MenuItem,
  useTheme,
  IconButton,
  FormControlLabel,
  Switch,
  Collapse,
  Alert,
  Chip,
  Tooltip,
  Checkbox,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import { ClapperboardIcon, UploadIcon } from 'lucide-react';
import { PhotoFilter, Repeat as RepeatIcon, Info as InfoIcon, Mic as MicIcon, Delete as DeleteIcon, CallSplit as CallSplitIcon, AssignmentReturn as RefundIcon } from '@mui/icons-material';
import ReceiptCapture from '../Receipts/ReceiptCapture';
import ReceiptGallery from '../Receipts/ReceiptGallery';
import VoiceTransactionButton from './VoiceTransactionButton';
import { useAccount } from '../../contexts/AccountContext';

const TransactionForm = ({ 
  open,
  onClose,
  onSave,
  onSubmit,
  onFormUpdate,
  initialData = {}, 
  categories = [],
  budgets = [],
  onCancel,
  existingTransaction = false
}) => {
  const { t } = useTranslation();
  
  // Helper function to get budgetMonth from a date
  const getBudgetMonthFromDate = (dateStr) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const defaultData = {
    description: '',
    amount: '',
    merchant: '',
    date: new Date().toISOString().split('T')[0],
    categoryId: categories.length > 0 ? categories[0].id : '',
    budgetMonth: getBudgetMonthFromDate(new Date().toISOString().split('T')[0]), // Auto-populate from date
    recurring: false,
    recurringConfig: {
      frequency: 'monthly',
      dayOfMonth: new Date().getDate(),
      endDate: '',
      isActive: true
    },
    ...(initialData || {})
  };
  
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [recurringPreview, setRecurringPreview] = useState([]);
  const { accounts, defaultAccount } = useAccount();
  const [accountId, setAccountId] = useState(initialData?.accountId || '');

  // --- Split transaction state ---
  const [isSplitEnabled, setIsSplitEnabled] = useState(false);
  const [splits, setSplits] = useState([{ categoryId: '', amount: '', note: '' }]);

  // --- Refund state ---
  const [refundOf, setRefundOf] = useState(initialData?.refundOf || null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundSearchQuery, setRefundSearchQuery] = useState('');
  const [refundSearchResults, setRefundSearchResults] = useState([]);
  const [refundSearchLoading, setRefundSearchLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:400px)');
  const isTinyScreen = useMediaQuery('(max-width:380px)');
  
  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...defaultData,
        ...initialData
      });
      setAccountId(initialData?.accountId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);
  
  // Auto-update budgetMonth when date changes (if user hasn't manually overridden)
  useEffect(() => {
    if (formData.date) {
      const calculatedBudgetMonth = getBudgetMonthFromDate(formData.date);
      // Only auto-update if budgetMonth hasn't been manually set to something different
      if (!formData.budgetMonthManuallySet) {
        setFormData(prev => ({
          ...prev,
          budgetMonth: calculatedBudgetMonth
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date]);
  
  // Auto-populate account from the category's fundingAccountId
  useEffect(() => {
    if (!formData.categoryId || accountId) return; // don't overwrite explicit selection
    const cat = categories.find(c => c.id === formData.categoryId || c._id === formData.categoryId);
    if (cat?.fundingAccountId) {
      setAccountId(cat.fundingAccountId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.categoryId]);

  // Update formData state setter to notify parent of changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value
    };
    
    // If budgetMonth is manually changed, mark it as manually set
    if (name === 'budgetMonth') {
      updatedData.budgetMonthManuallySet = true;
    }
    
    setFormData(updatedData);
    
    // Notify parent component if callback exists
    if (typeof onFormUpdate === 'function') {
      onFormUpdate(updatedData);
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  // Direct handler for type buttons - also update parent
  const handleTypeChange = (type) => {
    console.log('Setting transaction type to:', type);
    
    // If switching to income, clear the category
    const updatedData = {
      ...formData,
      type,
      // Clear categoryId if type is income
      categoryId: type === 'income' ? '' : formData.categoryId
    };
    
    setFormData(updatedData);

    // Pre-select default account when switching to income and no account is chosen
    if (type === 'income' && !accountId && defaultAccount?._id) {
      setAccountId(defaultAccount._id);
    }
    
    // Notify parent component if callback exists
    if (typeof onFormUpdate === 'function') {
      onFormUpdate(updatedData);
    }
  };

  // Handle recurring toggle
  const handleRecurringToggle = (event) => {
    const isRecurring = event.target.checked;
    setFormData({
      ...formData,
      recurring: isRecurring,
      recurringConfig: isRecurring ? {
        frequency: 'monthly',
        dayOfMonth: new Date(formData.date).getDate(),
        endDate: '',
        isActive: true
      } : null
    });
  };

  // Handle recurring config changes
  const handleRecurringConfigChange = (field, value) => {
    setFormData({
      ...formData,
      recurringConfig: {
        ...formData.recurringConfig,
        [field]: value
      }
    });
  };

  // --- Split handlers ---
  const handleSplitToggle = (e) => {
    setIsSplitEnabled(e.target.checked);
    if (!e.target.checked) {
      setSplits([{ categoryId: '', amount: '', note: '' }]);
    }
  };

  const handleSplitChange = (index, field, value) => {
    const updated = splits.map((s, i) => i === index ? { ...s, [field]: value } : s);
    setSplits(updated);
  };

  const handleAddSplit = () => {
    setSplits([...splits, { categoryId: '', amount: '', note: '' }]);
  };

  const handleRemoveSplit = (index) => {
    if (splits.length > 1) {
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  const splitsTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const splitMismatch = isSplitEnabled && formData.amount &&
    Math.abs(splitsTotal - parseFloat(formData.amount)) > 0.01;

  // --- Refund search ---
  const handleRefundSearch = async (query) => {
    setRefundSearchQuery(query);
    if (!query || query.length < 2) {
      setRefundSearchResults([]);
      return;
    }
    setRefundSearchLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`/api/transactions?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'x-auth-token': token }
      });
      if (res.ok) {
        const data = await res.json();
        const txList = Array.isArray(data) ? data : (data.transactions || []);
        setRefundSearchResults(txList.filter(t => !t.isRefunded).slice(0, 10));
      }
    } catch {
      setRefundSearchResults([]);
    } finally {
      setRefundSearchLoading(false);
    }
  };

  // Generate preview of recurring transactions
  const generateRecurringPreview = () => {
    if (!formData.recurring || !formData.amount) return;

    const preview = [];
    let currentDate = new Date(formData.date);
    const dayOfMonth = formData.recurringConfig.dayOfMonth;
    const frequency = formData.recurringConfig.frequency;
    const endDate = formData.recurringConfig.endDate ? new Date(formData.recurringConfig.endDate) : null;

    for (let i = 0; i < 6; i++) {
      // Calculate next occurrence based on frequency
      switch (frequency) {
        case 'weekly':
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 7));
          break;
        case 'biweekly':
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 14));
          break;
        case 'monthly':
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
          const maxDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
          currentDate.setDate(Math.min(dayOfMonth, maxDay));
          break;
        case 'quarterly':
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 3));
          break;
        case 'yearly':
          currentDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
          break;
        default:
          break;
      }

      if (endDate && currentDate > endDate) break;

      preview.push({
        date: new Date(currentDate),
        amount: formData.amount,
        description: formData.description
      });
    }

    setRecurringPreview(preview);
  };

  // Update preview when recurring settings change
  useEffect(() => {
    if (formData.recurring) {
      generateRecurringPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.recurring, formData.amount, formData.date, formData.recurringConfig]);

  const [attachedReceipt, setAttachedReceipt] = useState(null);
const [showReceiptScanner, setShowReceiptScanner] = useState(false);

// Add these handler functions inside your component:
const handleOpenReceiptScanner = () => {
  setShowReceiptScanner(true);
};

const handleReceiptUpload = (event) => {
  if (event.target.files && event.target.files[0]) {
    setAttachedReceipt(event.target.files[0]);
  }
};

const handleRemoveReceipt = () => {
  setAttachedReceipt(null);
};

const handleReceiptCaptured = (receiptData) => {
  setShowReceiptScanner(false);
  
  // If receiptData is a FormData object, we need to extract the file
  if (receiptData instanceof FormData) {
    const fileFromFormData = receiptData.get('receipt');
    if (fileFromFormData) {
      setAttachedReceipt(fileFromFormData);
    }
  } else {
    setAttachedReceipt(receiptData);
  }
};

// Add conditional rendering for the receipt scanner at the beginning of your return statement
if (showReceiptScanner) {
  return (
    <ReceiptCapture 
      onReceiptCaptured={handleReceiptCaptured}
      onCancel={() => setShowReceiptScanner(false)}
    />
  );
}

  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.amount || isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    // Only validate categoryId for expense transactions
    if (formData.type === 'expense' && !formData.categoryId) {
      newErrors.categoryId = 'Please select a category';
    }
    
    // Add validation for transaction type
    if (!formData.type) {
      newErrors.type = 'Please select whether this is Income or Expense';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // // Ensure the submit handler properly uses the callback
  // const handleSubmit = (e) => {
  //   if (e) e.preventDefault();
    
    
  //   console.log('TransactionForm handleSubmit called with data:', formData);
  //   console.log('Transaction type:', formData.type);
    
  //   if (validate()) {
  //     // Format the data before submitting
  //     const submissionData = {
  //       ...formData,
  //       amount: Number(formData.amount),
  //       // Explicitly include type to ensure it's not lost
  //       type: formData.type,
  //       // Make sure categoryId is empty for income
  //       categoryId: formData.type === 'income' ? '' : formData.categoryId
  //     };
      
  //     console.log('Submitting transaction:', submissionData);
      
  //     if (onSubmit) {
  //       onSubmit(submissionData);
  //     } else if (onSave) {
  //       onSave(submissionData);
  //     }
      
  //     return submissionData; // Return the data for potential direct use
  //   }
    
  //   return null; // Return null if validation fails
  // };

// Ensure the submit handler properly uses the callback
const handleSubmit = (e) => {
  if (e) e.preventDefault();
  
  console.log('TransactionForm handleSubmit called with data:', formData);
  console.log('Transaction type:', formData.type);
  
  if (validate()) {
    // Format the data before submitting
    const submissionData = {
      ...formData,
      amount: Number(formData.amount),
      // Explicitly include type to ensure it's not lost
      type: formData.type,
      // Make sure categoryId is empty for income
      categoryId: formData.type === 'income' ? '' : formData.categoryId,
      // Include selected account (null when unset)
      accountId: accountId || null,
      // New fields
      merchant: formData.merchant || '',
      splits: isSplitEnabled ? splits.map(s => ({
        categoryId: s.categoryId || undefined,
        amount: Number(s.amount),
        note: s.note || ''
      })) : [],
      refundOf: refundOf || undefined,
    };
    
    console.log('Submitting transaction:', submissionData);
    
    // If there's an attached receipt, we need to use FormData for the submission
    if (attachedReceipt) {
      const formDataObject = new FormData();
      
      // Add all the transaction data to the FormData
      Object.keys(submissionData).forEach(key => {
        formDataObject.append(key, submissionData[key]);
      });
      
      // Append the receipt file
      formDataObject.append('receipt', attachedReceipt);
      
      console.log('Submitting with receipt attachment');
      
      if (onSubmit) {
        onSubmit(formDataObject, true); // Pass true as second param to indicate FormData
      } else if (onSave) {
        onSave(formDataObject, true); // Pass true as second param to indicate FormData
      }
    } else {
      // No receipt, submit as regular JSON data
      if (onSubmit) {
        onSubmit(submissionData);
      } else if (onSave) {
        onSave(submissionData);
      }
    }
    
    return submissionData; // Return the data for potential direct use
  }
  
  return null; // Return null if validation fails
};


  // // Custom type selector with improved responsiveness
  // const renderTypeSelector = () => (
  //   <FormControl component="fieldset" fullWidth margin="normal" error={!!errors.type}>
  //     <Typography variant="subtitle2" gutterBottom>
  //       Transaction Type
  //     </Typography>
  //     <Stack 
  //       direction="row" 
  //       spacing={1}
  //       sx={{ width: '100%' }}
  //     >
  //       <Paper
  //         elevation={0}
  //         onClick={() => handleTypeChange('income')}
  //         sx={{
  //           flex: 1,
  //           p: { xs: 1, sm: 1.5 },
  //           textAlign: 'center',
  //           cursor: 'pointer',
  //           border: '2px solid',
  //           borderColor: formData.type === 'income' ? 'success.main' : errors.type ? 'error.main' : 'divider',
  //           bgcolor: formData.type === 'income' ? 'success.light' : 'background.paper',
  //           color: formData.type === 'income' ? 'success.contrastText' : 'text.primary',
  //           borderRadius: 1,
  //           fontWeight: formData.type === 'income' ? 'bold' : 'normal',
  //           fontSize: { xs: '0.9rem', sm: '1rem' },
  //           transition: 'all 0.2s',
  //           '&:hover': {
  //             bgcolor: formData.type === 'income' ? 'success.light' : 'action.hover',
  //             transform: 'translateY(-2px)'
  //           }
  //         }}
  //       >
  //         Income
  //       </Paper>
  //       <Paper
  //         elevation={0}
  //         onClick={() => handleTypeChange('expense')}
  //         sx={{
  //           flex: 1,
  //           p: { xs: 1, sm: 1.5 },
  //           textAlign: 'center',
  //           cursor: 'pointer',
  //           border: '2px solid',
  //           borderColor: formData.type === 'expense' ? 'error.main' : errors.type ? 'error.main' : 'divider',
  //           bgcolor: formData.type === 'expense' ? 'error.light' : 'background.paper',
  //           color: formData.type === 'expense' ? 'error.contrastText' : 'text.primary',
  //           borderRadius: 1,
  //           fontWeight: formData.type === 'expense' ? 'bold' : 'normal',
  //           fontSize: { xs: '0.9rem', sm: '1rem' },
  //           transition: 'all 0.2s',
  //           '&:hover': {
  //             bgcolor: formData.type === 'expense' ? 'error.light' : 'action.hover',
  //             transform: 'translateY(-2px)'
  //           }
  //         }}
  //       >
  //         Expense
  //       </Paper>
  //     </Stack>
      
  //     {/* Show error message if type is not selected */}
  //     {errors.type && (
  //       <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
  //         {errors.type}
  //       </Typography>
  //     )}
  //   </FormControl>
  // );


  // Custom type selector with improved responsiveness
const renderTypeSelector = () => (
  <FormControl component="fieldset" fullWidth margin="normal" error={!!errors.type}>
    <Typography variant="subtitle2" gutterBottom>
      Transaction Type
    </Typography>
    <Stack 
      direction="row" 
      spacing={1}
      sx={{ width: '100%' }}
    >
      <Paper
        elevation={0}
        onClick={() => handleTypeChange('income')}
        data-type-selector={formData.type === 'income' ? 'income' : ''}
        sx={{
          flex: 1,
          p: isTinyScreen ? { xs: 0.75, sm: 1 } : { xs: 1, sm: 1.5 },
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px solid',
          borderColor: formData.type === 'income' 
            ? (theme.palette.mode === 'dark' ? '#4caf50' : '#2e7d32') 
            : (errors.type ? 'error.main' : 'divider'),
          bgcolor: formData.type === 'income'
            ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.1)')
            : 'background.paper',
          color: formData.type === 'income'
            ? (theme.palette.mode === 'dark' ? '#e8f5e9' : '#2e7d32')
            : 'text.primary',
          borderRadius: 1,
          fontWeight: formData.type === 'income' ? 'bold' : 'normal',
          fontSize: isTinyScreen ? '0.8rem' : { xs: '0.9rem', sm: '1rem' },
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: formData.type === 'income'
              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(46, 125, 50, 0.2)')
              : 'action.hover',
            transform: isTinyScreen ? 'none' : 'translateY(-2px)'
          }
        }}
      >
        Income
      </Paper>
      <Paper
        elevation={0}
        onClick={() => handleTypeChange('expense')}
        data-type-selector={formData.type === 'expense' ? 'expense' : ''}
        sx={{
          flex: 1,
          p: isTinyScreen ? { xs: 0.75, sm: 1 } : { xs: 1, sm: 1.5 },
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px solid',
          borderColor: formData.type === 'expense'
            ? (theme.palette.mode === 'dark' ? '#f44336' : '#c62828')
            : (errors.type ? 'error.main' : 'divider'),
          bgcolor: formData.type === 'expense'
            ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(198, 40, 40, 0.1)')
            : 'background.paper',
          color: formData.type === 'expense'
            ? (theme.palette.mode === 'dark' ? '#ffebee' : '#c62828')
            : 'text.primary',
          borderRadius: 1,
          fontWeight: formData.type === 'expense' ? 'bold' : 'normal',
          fontSize: isTinyScreen ? '0.8rem' : { xs: '0.9rem', sm: '1rem' },
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: formData.type === 'expense'
              ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(198, 40, 40, 0.2)')
              : 'action.hover',
            transform: isTinyScreen ? 'none' : 'translateY(-2px)'
          }
        }}
      >
        Expense
      </Paper>
    </Stack>
    
    {/* Show error message if type is not selected */}
    {errors.type && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {errors.type}
      </Typography>
    )}
  </FormControl>
);
  
  // Form content with improved responsiveness
  const renderFormContent = () => (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {/* Warning for recurring template */}
      {formData.recurring && !formData.isRecurringInstance && formData.id && (
        <Alert severity="warning" icon={<RepeatIcon />} sx={{ fontSize: '0.85rem' }}>
          ⚠️ This is a recurring template. Changes will affect future instances.
        </Alert>
      )}
      
      {/* Info for auto-created instance */}
      {formData.isRecurringInstance && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ fontSize: '0.85rem' }}>
          ℹ️ Auto-created from recurring template
          {formData.recurringTemplateId && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Template ID: {formData.recurringTemplateId}
            </Typography>
          )}
        </Alert>
      )}
      
      {/* Type Selector */}
      <Box>
        {renderTypeSelector()}
      </Box>
      
      {/* Description Field with Voice Input */}
      <TextField
        fullWidth
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        error={!!errors.description}
        helperText={errors.description}
        placeholder={isTinyScreen ? "Transaction details" : "What was this transaction for?"}
        size={isTinyScreen || isMobile ? "small" : "medium"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <VoiceTransactionButton
                variant="inline"
                size="small"
                categories={categories}
                onTransactionSave={(data) => {
                  // Auto-fill form fields from voice data
                  setFormData(prev => ({
                    ...prev,
                    ...(data.type && { type: data.type }),
                    ...(data.amount && { amount: data.amount }),
                    ...(data.description && { description: data.description }),
                    ...(data.categoryId && { categoryId: data.categoryId }),
                    ...(data.date && { date: data.date }),
                    ...(data.budgetMonth && { budgetMonth: data.budgetMonth }),
                  }));
                }}
                onEditMore={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    ...(data.type && { type: data.type }),
                    ...(data.amount && { amount: data.amount }),
                    ...(data.description && { description: data.description }),
                    ...(data.categoryId && { categoryId: data.categoryId }),
                    ...(data.date && { date: data.date }),
                    ...(data.budgetMonth && { budgetMonth: data.budgetMonth }),
                  }));
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: isTinyScreen ? '0.9rem' : undefined
          }
        }}
      />

      {/* Merchant Field (optional) */}
      <TextField
        fullWidth
        label="Merchant (optional)"
        name="merchant"
        value={formData.merchant || ''}
        onChange={handleChange}
        placeholder="e.g. Amazon, Lidl, Netflix"
        size={isTinyScreen || isMobile ? "small" : "medium"}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: isTinyScreen ? '0.9rem' : undefined
          }
        }}
      />
      
      {/* Amount and Date Fields - Stack vertically on very small screens */}
      <Stack 
        direction={isTinyScreen || isExtraSmall ? "column" : "row"} 
        spacing={isTinyScreen ? 1.5 : 2}
        sx={{ width: '100%' }}
      >
        <TextField
          fullWidth
          label="Amount"
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          error={!!errors.amount}
          helperText={errors.amount}
          InputProps={{
            startAdornment: <InputAdornment position="start">€</InputAdornment>
          }}
          size={isTinyScreen || isMobile ? "small" : "medium"}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: isTinyScreen ? '0.9rem' : undefined
            }
          }}
        />
        
        <TextField
          fullWidth
          label="Date"
          name="date"
          type="date"
          value={formData.date}
          onChange={handleChange}
          error={!!errors.date}
          helperText={errors.date}
          InputLabelProps={{
            shrink: true,
          }}
          size={isTinyScreen || isMobile ? "small" : "medium"}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: isTinyScreen ? '0.9rem' : undefined
            }
          }}
        />
      </Stack>

      {/* ── Split Transaction Section ── */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={isSplitEnabled}
              onChange={handleSplitToggle}
              size="small"
              color="primary"
              icon={<CallSplitIcon fontSize="small" />}
              checkedIcon={<CallSplitIcon fontSize="small" color="primary" />}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">Split this transaction</Typography>
            </Box>
          }
        />

        <Collapse in={isSplitEnabled}>
          <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Divide this transaction across multiple categories
            </Typography>
            <List disablePadding>
              {splits.map((split, idx) => (
                <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="center">
                    {/* Category picker */}
                    <FormControl size="small" sx={{ flex: 2 }}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={split.categoryId}
                        label="Category"
                        onChange={(e) => handleSplitChange(idx, 'categoryId', e.target.value)}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cat.color || '#1976d2' }} />
                              {cat.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* Amount */}
                    <TextField
                      size="small"
                      label="Amount"
                      type="number"
                      value={split.amount}
                      onChange={(e) => handleSplitChange(idx, 'amount', e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      sx={{ flex: 1 }}
                    />
                    {/* Note */}
                    <TextField
                      size="small"
                      label="Note"
                      value={split.note}
                      onChange={(e) => handleSplitChange(idx, 'note', e.target.value)}
                      sx={{ flex: 2 }}
                    />
                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSplit(idx)}
                      disabled={splits.length === 1}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}
            </List>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Button size="small" onClick={handleAddSplit} variant="outlined">
                + Add split
              </Button>
              <Typography
                variant="caption"
                color={splitMismatch ? 'error' : 'text.secondary'}
                fontWeight={splitMismatch ? 'bold' : undefined}
              >
                {splitsTotal.toFixed(2)} / {parseFloat(formData.amount || 0).toFixed(2)} €
                {splitMismatch && ' ← must match'}
              </Typography>
            </Stack>

            {splitMismatch && (
              <Alert severity="error" sx={{ mt: 1, fontSize: '0.8rem' }}>
                Split totals must equal the transaction amount.
              </Alert>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ── Refund Link ── */}
      <Box>
        {refundOf ? (
          <Alert
            severity="info"
            sx={{ fontSize: '0.82rem' }}
            action={
              <Button size="small" color="inherit" onClick={() => setRefundOf(null)}>
                Clear
              </Button>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RefundIcon fontSize="small" />
              Refund of transaction: <strong>{refundOf._id || refundOf}</strong>
            </Box>
          </Alert>
        ) : (
          <Button
            size="small"
            startIcon={<RefundIcon />}
            variant="outlined"
            color="secondary"
            onClick={() => setRefundDialogOpen(true)}
          >
            Mark as refund of…
          </Button>
        )}
      </Box>

      {/* Refund search dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select original transaction to refund</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search transactions"
            size="small"
            value={refundSearchQuery}
            onChange={(e) => handleRefundSearch(e.target.value)}
            placeholder="Type description or amount…"
            sx={{ mt: 1, mb: 1 }}
          />
          {refundSearchLoading && (
            <Typography variant="caption" color="text.secondary">Searching…</Typography>
          )}
          {refundSearchResults.length === 0 && !refundSearchLoading && refundSearchQuery.length >= 2 && (
            <Typography variant="caption" color="text.secondary">No transactions found.</Typography>
          )}
          <List disablePadding>
            {refundSearchResults.map((tx) => (
              <ListItem
                key={tx._id}
                button
                onClick={() => {
                  setRefundOf(tx._id);
                  setRefundDialogOpen(false);
                  setRefundSearchQuery('');
                  setRefundSearchResults([]);
                }}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight="bold">{tx.description}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(tx.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="error.main">
                      €{tx.amount}
                    </Typography>
                    {tx.merchant && (
                      <Typography variant="caption" color="text.secondary">
                        {tx.merchant}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Budget Month Picker - Optional override */}
      <Box sx={{ mt: 1 }}>
        <TextField
          fullWidth
          label="Budget Month"
          name="budgetMonth"
          type="month"
          value={formData.budgetMonth || ''}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
          size={isTinyScreen || isMobile ? "small" : "medium"}
          helperText="Assign this transaction to a different budget month (optional)"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: isTinyScreen ? '0.9rem' : undefined
            }
          }}
        />
        {formData.budgetMonth && formData.date && getBudgetMonthFromDate(formData.date) !== formData.budgetMonth && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip 
              label={`💰 Budgeted for ${new Date(formData.budgetMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        )}
      </Box>
      
      {/* Category Selection - Only show for expense transactions */}
      {formData.type === 'expense' && (
        <FormControl fullWidth error={!!errors.categoryId} size={isTinyScreen || isMobile ? "small" : "medium"}>
          <InputLabel id="category-label">{t('transactions.form.category', 'Category')}</InputLabel>
          <Select
            labelId="category-label"
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            label={t('transactions.form.category', 'Category')}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: isTinyScreen ? '0.9rem' : undefined
              }
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: { xs: 10, sm: 12 }, 
                      height: { xs: 10, sm: 12 }, 
                      borderRadius: '50%', 
                      bgcolor: category.color || '#1976d2',
                      mr: 1 
                    }} 
                  />
                  {category.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.categoryId && (
            <Typography variant="caption" color="error">
              {errors.categoryId}
            </Typography>
          )}
        </FormControl>
      )}

      {/* Account picker — optional */}
      <FormControl fullWidth size={isTinyScreen || isMobile ? 'small' : 'medium'}>
        <InputLabel id="account-label">{t('accounts.assignToTransaction', 'Account')}</InputLabel>
        <Select
          labelId="account-label"
          value={accountId}
          label={t('accounts.assignToTransaction', 'Account')}
          onChange={(e) => setAccountId(e.target.value)}
          sx={{ '& .MuiInputBase-input': { fontSize: isTinyScreen ? '0.9rem' : undefined } }}
        >
          <MenuItem value="">
            <em>{t('accounts.unassigned', 'Unassigned')}</em>
          </MenuItem>
          {accounts.map((acc) => (
            <MenuItem key={acc._id} value={acc._id}>
              {acc.icon || '🏦'} {acc.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Recurring Transaction Section */}
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.recurring || false}
              onChange={handleRecurringToggle}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RepeatIcon fontSize="small" />
              <Typography variant="body2">Make this a recurring transaction</Typography>
            </Box>
          }
        />

        <Collapse in={formData.recurring}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, fontSize: '0.85rem' }}>
              Set up automatic recurring transactions for regular bills, subscriptions, or monthly payments.
            </Alert>

            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('transactions.recurring.frequency', 'Frequency')}</InputLabel>
                <Select
                  value={formData.recurringConfig?.frequency || 'monthly'}
                  onChange={(e) => handleRecurringConfigChange('frequency', e.target.value)}
                  label={t('transactions.recurring.frequency', 'Frequency')}
                >
                  <MenuItem value="weekly">{t('transactions.recurring.weekly', 'Weekly')}</MenuItem>
                  <MenuItem value="biweekly">{t('transactions.recurring.biweekly', 'Bi-weekly (Every 2 weeks)')}</MenuItem>
                  <MenuItem value="monthly">{t('transactions.recurring.monthly', 'Monthly')}</MenuItem>
                  <MenuItem value="quarterly">{t('transactions.recurring.quarterly', 'Quarterly (Every 3 months)')}</MenuItem>
                  <MenuItem value="yearly">{t('transactions.recurring.yearly', 'Yearly')}</MenuItem>
                </Select>
              </FormControl>

              {['monthly', 'quarterly', 'yearly'].includes(formData.recurringConfig?.frequency) && (
                <TextField
                  fullWidth
                  size="small"
                  label="Day of Month"
                  type="number"
                  value={formData.recurringConfig?.dayOfMonth || new Date().getDate()}
                  onChange={(e) => handleRecurringConfigChange('dayOfMonth', parseInt(e.target.value))}
                  InputProps={{
                    inputProps: { min: 1, max: 31 }
                  }}
                  helperText="Which day of the month should this recur?"
                />
              )}

              <TextField
                fullWidth
                size="small"
                label="End Date (Optional)"
                type="date"
                value={formData.recurringConfig?.endDate || ''}
                onChange={(e) => handleRecurringConfigChange('endDate', e.target.value)}
                InputLabelProps={{
                  shrink: true
                }}
                helperText="Leave empty for indefinite recurrence"
              />

              {recurringPreview.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    📅 Next 6 occurrences:
                  </Typography>
                  <Stack spacing={0.5}>
                    {recurringPreview.map((item, index) => (
                      <Chip
                        key={index}
                        label={`${item.date.toLocaleDateString('it-IT')} - €${item.amount}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Alert severity="success" sx={{ fontSize: '0.8rem' }}>
                <Typography variant="caption">
                  This transaction will be automatically created {formData.recurringConfig?.frequency === 'monthly' ? 'every month' : 
                  formData.recurringConfig?.frequency === 'weekly' ? 'every week' : 
                  formData.recurringConfig?.frequency === 'biweekly' ? 'every 2 weeks' :
                  formData.recurringConfig?.frequency === 'quarterly' ? 'every 3 months' :
                  'every year'}
                  {formData.recurringConfig?.endDate && ` until ${new Date(formData.recurringConfig.endDate).toLocaleDateString('it-IT')}`}
                  {!formData.recurringConfig?.endDate && ' indefinitely'}.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        </Collapse>
      </Box>

      {/* Desktop buttons */}
      {!isMobile && onCancel && (
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="flex-end" 
          sx={{ mt: 2 }}
        >
          <Button 
            variant="outlined" 
            color="inherit" 
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={splitMismatch}
          >
            {existingTransaction ? 'Update' : 'Add'} Transaction
          </Button>
        </Stack>
      )}
    </Stack>
  );

  // If we're being used in a dialog (not in a bottom sheet)
  if (open !== undefined) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isTinyScreen}
        PaperProps={{
          sx: {
            borderRadius: isTinyScreen ? 0 : { xs: 1, sm: 2 },
            width: isTinyScreen ? '100%' : { xs: '95%', sm: '80%' },
            maxWidth: isTinyScreen ? 'none' : 600,
            height: isTinyScreen ? '100%' : 'auto',
            margin: isTinyScreen ? 0 : 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: isTinyScreen ? '1rem' : { xs: '1.1rem', sm: '1.25rem' },
          py: isTinyScreen ? 1.5 : 2,
          px: isTinyScreen ? 2 : 3
        }}>
          {existingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ 
            pt: isTinyScreen ? 1 : { xs: 1, sm: 2 }, 
            px: isTinyScreen ? 1 : { xs: 0.5, sm: 1 } 
          }}>
            {renderFormContent()}
          </Box>
          {/* Receipt gallery — only shown when editing an existing transaction */}
          {existingTransaction && initialData?._id && (
            <Box sx={{ px: isTinyScreen ? 1 : { xs: 0.5, sm: 1 }, pb: 1 }}>
              <ReceiptGallery transactionId={initialData._id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: isTinyScreen ? 1.5 : { xs: 1.5, sm: 2 },
          gap: isTinyScreen ? 1 : 0
        }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            size={isTinyScreen ? "small" : "medium"}
            sx={{ minWidth: isTinyScreen ? 'auto' : 64 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            size={isTinyScreen ? "small" : "medium"}
            sx={{ minWidth: isTinyScreen ? 'auto' : 64 }}
          >
            {existingTransaction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // In your TransactionForm component
// Add a receipt attachment option
<Box sx={{ mt: 3 }}>
  <Typography variant="subtitle2" gutterBottom>
    Attach Receipt
  </Typography>
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Button
      variant="outlined"
      startIcon={<PhotoFilter />}
      onClick={handleOpenReceiptScanner}
    >
      Scan Receipt
    </Button>
    <Button
      variant="outlined"
      startIcon={<UploadIcon />}
      component="label"
    >
      Upload Receipt
      <input
        type="file"
        hidden
        accept="image/*"
        onChange={handleReceiptUpload}
      />
    </Button>
  </Box>
  
  {/* Preview of attached receipt */}
  {attachedReceipt && (
    <Box sx={{ mt: 2, position: 'relative' }}>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box
          component="img"
          src={URL.createObjectURL(attachedReceipt)}
          alt="Receipt"
          sx={{
            width: 60,
            height: 60,
            objectFit: 'cover',
            borderRadius: 1,
            mr: 2
          }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" noWrap>
            {attachedReceipt.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(attachedReceipt.size / 1024).toFixed(1)} KB
          </Typography>
        </Box>
        <IconButton onClick={handleRemoveReceipt} size="small">
          <ClapperboardIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  )}
</Box>
  
  // For use in bottom sheet
  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 1, sm: 2 } }}>
      {renderFormContent()}
      
      {/* Mobile buttons - when used in a bottom sheet */}
      {isMobile && onCancel && (
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="space-between" 
          sx={{ mt: 3, mb: 2 }}
        >
          <Button 
            variant="outlined" 
            color="inherit" 
            onClick={onCancel}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            sx={{ flex: 1 }}
          >
            {existingTransaction ? 'Update' : 'Add'}
          </Button>
        </Stack>
      )}
    </Box>

  );

  

};

export default TransactionForm;